import { Form, Loading, Modal } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { useShareComment } from '@/features/comment/hooks/comment';
import {
  type CommentModel,
  ShareCommentFormSchema,
} from '@/features/comment/models/comment';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { CommentStrictFields } from '../common/CommentStrictFields';

const FORM_ID = 'comment-publication-form';

type Props = {
  onClose: () => void;
  comment: z.infer<typeof CommentModel>;
};

type FormValues = z.infer<typeof ShareCommentFormSchema>;

export const CommentPublicationModal: FC<Props> = ({ onClose, ...props }) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const me = useMe();

  const formMethods = useForm<FormValues>({
    resolver: zodResolver(ShareCommentFormSchema),
    defaultValues: {
      relatedUserIds: props.comment.relatedUserIds,
      sharedOrganizationIds: props.comment.sharedOrganizationIds,
      assigneeIds: props.comment.assigneeIds,
    },
  });

  const {
    reset,
    formState: { isSubmitting },
    handleSubmit,
  } = formMethods;

  const { mutateAsync: shareCommentAsync, isPending: isShareCommentLoading } =
    useShareComment();

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      await shareCommentAsync({
        ...values,
        id: props.comment.id,
      });
      openMessage('success', t('message-save-completed'));
      onClose();
    } catch {
      openMessage('error', t('message-save-failed'));
    }
  };

  return (
    <Modal
      centered
      destroyOnClose
      title={t('comment.comment-publication')}
      open={true}
      okText={t('save')}
      okButtonProps={{
        loading: isSubmitting || isShareCommentLoading,
        htmlType: 'submit',
        form: FORM_ID,
      }}
      onCancel={onClose}
      afterOpenChange={(value) => {
        !value && reset();
      }}
    >
      {me.isPending || !me.data ? (
        <Loading />
      ) : (
        <FormProvider {...formMethods}>
          <Form
            leavePageAlertCondition={null}
            layout="vertical"
            id={FORM_ID}
            onFinish={handleSubmit(onSubmit)}
          >
            <CommentStrictFields />
          </Form>
        </FormProvider>
      )}
    </Modal>
  );
};
